import { useEffect, useRef } from 'react';

/**
 * 전역 starfield 배경 — Canvas 2D. AppShell 최하단 고정 레이어.
 * 느리게 드리프트하는 별 모트 + 패럴랙스. reduced-motion 시 정적.
 * 한 개만 존재(전역). 별밭은 분위기 레이어라 WebGL/Pixi를 초기 번들에 싣지 않는다.
 */
export function Starfield() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    let w = 0;
    let h = 0;

    type Star = { x: number; y: number; r: number; a: number; tw: number; speed: number; color: string };
    const stars: Star[] = [];

    const seedStars = () => {
      stars.length = 0;
      const conf = [
        { count: 70, size: [0.45, 0.9], alpha: 0.34, speed: 0.012 },
        { count: 45, size: [0.8, 1.4], alpha: 0.52, speed: 0.022 },
        { count: 22, size: [1.2, 2.1], alpha: 0.72, speed: 0.04 },
      ] as const;
      for (const layer of conf) {
        for (let i = 0; i < layer.count; i++) {
          stars.push({
            x: Math.random() * w,
            y: Math.random() * h,
            r: layer.size[0] + Math.random() * (layer.size[1] - layer.size[0]),
            a: layer.alpha,
            tw: Math.random() * Math.PI * 2,
            speed: layer.speed,
            color: Math.random() > 0.82 ? '#a6b2ff' : Math.random() > 0.5 ? '#74cfe9' : '#ffffff',
          });
        }
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = Math.max(1, rect.width);
      h = Math.max(1, rect.height);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedStars();
    };

    const draw = (t = 0) => {
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        if (!reduce) {
          s.y += s.speed;
          if (s.y > h + 2) s.y = -2;
        }
        const alpha = reduce ? s.a : s.a * (0.55 + 0.45 * (0.5 + 0.5 * Math.sin(t * 0.0015 + s.tw)));
        ctx.globalAlpha = alpha;
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (!reduce) raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    const ro = new ResizeObserver(() => {
      resize();
      draw();
    });
    ro.observe(canvas);

    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 size-full"
      style={{ contain: 'strict' }}
    />
  );
}
